import type {
  ApiContext,
  ApiNotAuthorized,
  BreakPatch,
  InvalidFormField,
  LiveBreakHost,
  LiveBreakHostDetail,
  LiveCancelledBreak,
  LiveChatMessage,
  LiveCsv,
  LiveIngestCredentials,
  LiveLabel,
  LivePackingSlip,
  LiveParsedSpots,
  LivePromotedBreak,
  LiveReveal,
  LiveSegmentsAdded,
  LiveShipmentAddressSaved,
  LiveSpotList,
  LiveSpotTemplate,
  LiveStage,
  LiveStoreShipping,
  LiveStream,
  LiveStreamHostDetail,
  LiveStreamViewers,
  LiveSyncRecording,
  LiveSyncStream,
  NewBreaks,
  NewSegment,
  NewSpotList,
  ShippingAddress,
  StoreNotFound,
  StoreRoleForbidden,
  StreamNotFound,
  UploadedImage,
} from "@hobbyshopify/backend-client";
import { LiveRefused } from "@hobbyshopify/backend-client";
import { type AsyncResult, getError } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { shopEntryId } from "../primitives/workspace";
import type { BackendApi } from "./api";

type Calls = { client: BackendApi; authHandler: AuthHandler<ApiContext> };

export function loadStreams(
  o: Calls & { shopId: string; locationId: string | null },
): AsyncResult<LiveStream[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.live.streams(ctx, shopEntryId(o.shopId, o.locationId)));
}

/**
 * Go live at a location: opens a stream hosted by the caller, or finds the
 * one already open there (a location has one). `created` says which.
 */
export async function openStream(
  o: Calls & { shopId: string; locationId: string; title: string },
): AsyncResult<
  { streamId: string; created: boolean },
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | LiveRefused
> {
  const r = await o.authHandler.callApi((ctx) =>
    o.client.live.createStream(ctx, o.shopId, { locationId: o.locationId, title: o.title }),
  );
  if (r.ok) return { ok: true, value: { streamId: r.value.id, created: true } };
  const open = getError(r, LiveRefused);
  const existing = open?.code === "location_has_stream" ? open.details.streamId : undefined;
  if (typeof existing === "string") return { ok: true, value: { streamId: existing, created: false } };
  return r;
}

/** Appends to the line-up. On a live stream the boxes' spot holders are pushed at once. */
export function addSegments(
  o: Calls & { streamId: string; items: NewSegment[] },
): AsyncResult<LiveSegmentsAdded, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.addSegments(ctx, o.streamId, o.items));
}

/** A planned segment out of the line-up; a box goes back to the queue, still selling. */
export function removeSegment(
  o: Calls & { streamId: string; segmentId: string },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.removeSegment(ctx, o.streamId, o.segmentId));
}

export function reorderSegments(
  o: Calls & { streamId: string; orderedSegmentIds: string[] },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.reorderSegments(ctx, o.streamId, o.orderedSegmentIds));
}

/** On screen now; `null` clears the screen. */
export function setCurrentSegment(
  o: Calls & { streamId: string; segmentId: string | null },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.setCurrentSegment(ctx, o.streamId, o.segmentId));
}

/** One spot of the break on screen singled out for viewers; `null` unpins. */
export function pinSpot(
  o: Calls & { streamId: string; slotId: string | null },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.pinSpot(ctx, o.streamId, o.slotId));
}

/** The caller becomes the host: theirs to draw, finish and end. */
export function takeOverStream(o: Calls & { streamId: string }): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.takeOver(ctx, o.streamId));
}

/** The shop's queue as the host sees it: every unfinished break, fullest first. */
export function loadHostQueue(
  o: Calls & { shopId: string },
): AsyncResult<LiveBreakHost[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.live.hostQueue(ctx, o.shopId));
}

/** Puts a box on sale in the shop's queue, with no stream. */
export function addBreaks(
  o: Calls & { shopId: string; breaks: NewBreaks },
): AsyncResult<string[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.addBreaks(ctx, o.shopId, o.breaks));
}

export function loadSpotTemplates(o: Calls): AsyncResult<LiveSpotTemplate[], ApiNotAuthorized> {
  return o.authHandler.callApi((ctx) => o.client.live.spotTemplates(ctx));
}

/** Built-in leagues, the curated library and the shop's own saved lists. */
export function loadSpotLists(
  o: Calls & { shopId: string },
): AsyncResult<LiveSpotList[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.live.spotLists(ctx, o.shopId));
}

export function saveSpotList(
  o: Calls & { shopId: string; list: NewSpotList },
): AsyncResult<LiveSpotList, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.saveSpotList(ctx, o.shopId, o.list));
}

export function deleteSpotList(
  o: Calls & { shopId: string; spotListId: string },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.deleteSpotList(ctx, o.shopId, o.spotListId));
}

export function parseSpots(
  o: Calls & { text: string },
): AsyncResult<LiveParsedSpots, ApiNotAuthorized | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.live.parseSpots(ctx, o.text));
}

/** `image` is a `data:` URL; `readImageFile` in `core/primitives/image-upload.ts` makes one. */
export function uploadLiveCover(
  o: Calls & { image: string },
): AsyncResult<UploadedImage, ApiNotAuthorized | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.uploads.liveCover(ctx, o.image));
}

export function loadStreamHostDetail(
  o: Calls & { streamId: string },
): AsyncResult<LiveStreamHostDetail, ApiNotAuthorized | StreamNotFound> {
  return o.authHandler.callApi((ctx) => o.client.live.streamHostDetail(ctx, o.streamId));
}

export function loadStreamViewers(
  o: Calls & { streamId: string },
): AsyncResult<LiveStreamViewers, ApiNotAuthorized | StreamNotFound> {
  return o.authHandler.callApi((ctx) => o.client.live.streamViewers(ctx, o.streamId));
}

/** Hands up and guests on the stream. */
export function loadStage(o: Calls & { streamId: string }): AsyncResult<LiveStage, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.stage(ctx, o.streamId));
}

/** Asks a buyer with a hand up to come on; they still have to accept. */
export function inviteGuest(
  o: Calls & { streamId: string; userId: string },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.inviteGuest(ctx, o.streamId, o.userId));
}

/** Takes a guest off, withdraws an invitation, or drops a hand. */
export function removeGuest(
  o: Calls & { streamId: string; userId: string },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.removeGuest(ctx, o.streamId, o.userId));
}

/** Mutes a guest's microphone; only the guest can unmute. */
export function muteGuest(
  o: Calls & { streamId: string; userId: string },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.muteGuest(ctx, o.streamId, o.userId));
}

export function loadBreakHostDetail(
  o: Calls & { breakId: string },
): AsyncResult<LiveBreakHostDetail, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.breakHostDetail(ctx, o.breakId));
}

/** The title, tile image or break time; answers the break read back. */
export function updateBreak(
  o: Calls & { breakId: string; patch: BreakPatch },
): AsyncResult<LiveBreakHostDetail, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.updateBreak(ctx, o.breakId, o.patch));
}

export function provisionIngest(
  o: Calls & { streamId: string },
): AsyncResult<LiveIngestCredentials, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.provisionIngest(ctx, o.streamId));
}

export function endStream(
  o: Calls & { streamId: string; finishRipping?: boolean },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.endStream(ctx, o.streamId, { finishRipping: o.finishRipping }));
}

export function syncStream(
  o: Calls & { streamId: string },
): AsyncResult<LiveSyncStream, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.syncStream(ctx, o.streamId));
}

export function syncRecording(
  o: Calls & { streamId: string },
): AsyncResult<LiveSyncRecording, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.syncRecording(ctx, o.streamId));
}

export function publishBreak(o: Calls & { breakId: string }): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.publishBreak(ctx, o.breakId));
}

/** Pushes the network that the box still has spots; once a day per break. */
export function promoteBreak(
  o: Calls & { breakId: string },
): AsyncResult<LivePromotedBreak, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.promoteBreak(ctx, o.breakId));
}

/** Draws the box on screen. `force` is the host confirming a draw before its break time. */
export function randomizeBreak(
  o: Calls & { breakId: string; force?: boolean },
): AsyncResult<LiveReveal, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.randomizeBreak(ctx, o.breakId, { force: o.force }));
}

export function finishBreak(o: Calls & { breakId: string }): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.finishBreak(ctx, o.breakId));
}

/** Refunds every paid spot in full; `refundsFailed` counts the buyers still owed. */
export function cancelBreak(
  o: Calls & { breakId: string },
): AsyncResult<LiveCancelledBreak, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.cancelBreak(ctx, o.breakId));
}

export function loadChat(
  o: Calls & { streamId: string },
): AsyncResult<LiveChatMessage[], ApiNotAuthorized | StreamNotFound> {
  return o.authHandler.callApi((ctx) => o.client.live.chat(ctx, o.streamId));
}

export function postChatMessage(
  o: Calls & { streamId: string; text: string },
): AsyncResult<LiveChatMessage, ApiNotAuthorized | StreamNotFound | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.live.postChatMessage(ctx, o.streamId, o.text));
}

export function hideChatMessage(
  o: Calls & { streamId: string; messageId: string },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.hideChatMessage(ctx, o.streamId, o.messageId));
}

/** The Shipping tab: open parcels, refunds owed. */
export function loadStoreShipping(
  o: Calls & { shopId: string; locationId: string | null },
): AsyncResult<LiveStoreShipping, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.live.storeShipping(ctx, shopEntryId(o.shopId, o.locationId)));
}

export function shippingCsv(
  o: Calls & { shopId: string; locationId: string | null },
): AsyncResult<LiveCsv, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.live.shippingCsv(ctx, shopEntryId(o.shopId, o.locationId)));
}

export function setShipmentAddress(
  o: Calls & { shipmentId: string; address: ShippingAddress },
): AsyncResult<LiveShipmentAddressSaved, ApiNotAuthorized | LiveRefused | InvalidFormField> {
  return o.authHandler.callApi((ctx) => o.client.live.setShipmentAddress(ctx, o.shipmentId, o.address));
}

export function buyShipmentLabel(
  o: Calls & { shipmentId: string; senderPhone?: string },
): AsyncResult<LiveLabel, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.buyShipmentLabel(ctx, o.shipmentId, o.senderPhone));
}

export function markShipmentShipped(
  o: Calls & { shipmentId: string },
): AsyncResult<void, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.markShipmentShipped(ctx, o.shipmentId));
}

export function packingSlip(
  o: Calls & { shipmentId: string },
): AsyncResult<LivePackingSlip, ApiNotAuthorized | LiveRefused> {
  return o.authHandler.callApi((ctx) => o.client.live.packingSlip(ctx, o.shipmentId));
}
